import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { formatAppointmentDate, formatAppointmentTime } from '../../../utils/appointment-time';
import Grid from '../../Grid';
import { useForm } from 'react-hook-form';
import styles from './Checkout.module.scss'
import CardForm from './CardForm';
import CustomerInfo from './CustomerInfo';
import ShippingForm from './ShippingForm';
import { CustomerContext } from "../../../contexts/CustomerContext";
import { useHistory, useLocation } from "react-router-dom";
import Cart from '../../../models/Cart';
import Button from "../../Button";
import acceptedCards from '../../../assets/accepted_cards.png';
import Image from "../../Image";
import Hr from "../../Hr";
import PayPal from './PayPal';
import ErrorContainer from "./ErrorContainer";
import Stripe from "./Stripe";
import { AfterpayClearpayMessageElement } from '@stripe/react-stripe-js';
import { getStates } from '../../../api/states';
import { createOrder } from '../../../api/orders'
import afterpayLogo from '../../../assets/afterpay.png';
import useQueryString from '../../../hooks/use-query-string'
import { useStripe } from '@stripe/react-stripe-js';
import { fetchPaymentIntent, handleClearpay, validateNewOrder,
  onPayPalApprove, validateCustomerInfo, validatePaymentMethod, assignOrderData } from './Utils';
import cn from 'classnames';
import AppointmentSection from './AppointmentSection';
import routes from '../../../routes';
import CouponForm from '../order-review/CouponForm';
import { computeSummaryTotals } from './summaryTotals';
import { canOfferAfterpay } from '../../../utils/afterpayAvailability';
import { toCents } from '../../../utils/toCents';

const useCart = (customer, refreshKey = 0) => {
  const [cart, setCart] = useState();
  const { app } = useQueryString()
  const cartKey = app ? 'apptCart' : 'cart'

  useEffect(() => {
    const { priorityCart } = customer

    if (!customer[cartKey]) return;

    Cart
      .where({ for_email: [(priorityCart && priorityCart.id) || customer[cartKey].id, customer.email] })
      .includes({ orderPackages: [{ student: 'school' }, 'apiOption'] })
      .first()
      .then(response => {
        setCart(response.data);
      });
  // Re-fetch when refreshKey changes (e.g. a coupon was applied in place, so the
  // discounted totals need to reload without a full page reload).
  }, [refreshKey]);

  return cart;
};

const usePackages = (cart, customer) => {
  const [packages, setPackages] = useState([])

  useEffect(() => {
    if (!cart) return

    Cart
      .includes({ orderPackages: 'package' })
      .where({ for_email: [cart.id, customer.email] })
      .first()
      .then((res) => {
        setPackages(res.data.orderPackages.map((orderPackage) => orderPackage.package))
      })
  }, [cart && cart.id])

  return packages
}

// Feature-icon helpers — mirror the icons used on the choose-your-session
// page so the summary keeps visual continuity through the booking flow.
const CapIcon = () => (
  <svg className={styles.seniorFeatureIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
  </svg>
)
const BookIcon = () => (
  <svg className={styles.seniorFeatureIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 4H3c-1.1 0-2 .9-2 2v13c0 1.1.9 2 2 2h6c.55 0 1.05-.22 1.41-.59L12 19l1.59 1.41c.36.37.86.59 1.41.59h6c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM3 19V6h8v13.08L9.92 18H3zm18 0h-6.92L13 19.08V6h8v13z"/>
  </svg>
)
const CameraIcon = () => (
  <svg className={styles.seniorFeatureIcon} viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" aria-hidden="true">
    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
    <circle cx="12" cy="13" r="2.6"/>
  </svg>
)
const featureIconFor = (label) => {
  if (/cap.*?gown/i.test(label)) return <CapIcon />
  if (/yearbook.*?pose/i.test(label)) return <BookIcon />
  if (/poses?\b/i.test(label)) return <CameraIcon />
  return null
}

// Demo data used when there's no real cart on localhost — lets the design
// of the summary be reviewed without booking through the full flow.
const DEMO_SUMMARY = {
  studentName: 'Jordan Sample',
  sessionName: 'Deluxe Session',
  features: ['20 Poses', 'Yearbook Poses', 'Cap & Gown', 'Plus 1 Personal Outfit'],
  price: 59.95,
  date: 'Tuesday, September 8, 2026',
  time: '9:00 AM',
  schoolName: '_Test High School',
  location: '123 School Lane, Modesto, CA',
}

// Demo-only feature mapping. The real-flow review reads features from the
// stored OrderPackage's apiOption.description, so this is only used to make
// the localhost preview show realistic content when ?demoSession=Super is
// in the URL. Mirrors `SESSION_FEATURES` in seniors_controller.rb.
const DEMO_FEATURES_BY_SESSION = {
  'Super Session':    ['30 Poses', 'Yearbook Poses', 'Cap & Gown', 'Plus 2 Personal Outfits'],
  'Deluxe Session':   ['20 Poses', 'Yearbook Poses', 'Cap & Gown', 'Plus 1 Personal Outfit'],
  'Standard Session': ['16 Poses', 'Yearbook Pose', 'Cap & Gown'],
  'Basic Session':    ['12 Poses', 'Yearbook Poses Only'],
  'Economy Session':  ['6 Poses', 'Yearbook Poses Only'],
}

const formatPrice = (n) => `$${Number(n || 0).toFixed(2)}`

// Parse a demo time string in the format "YYYY-MM-DD HH:MM AM/PM" — the
// shape the SeniorPickerDemo emits — into display strings matching the rest
// of the summary. Used only when the localhost Checkout falls back to demo
// data because no real cart was saved.
const parseDemoTime = (demoTime) => {
  if (!demoTime || typeof demoTime !== 'string') return null
  const parts = demoTime.trim().split(' ')
  if (parts.length < 3) return null
  const [dateStr, hhmm, suffix] = parts
  const m = moment.utc(dateStr, 'YYYY-MM-DD')
  if (!m.isValid()) return null
  const date = m.format('dddd, MMMM D, YYYY')
  const hourMatch = hhmm && hhmm.match(/^(\d{1,2}):(\d{2})$/)
  if (!hourMatch) return null
  const hour = parseInt(hourMatch[1], 10)   // strip leading zero
  const minute = hourMatch[2]
  const time = `${hour}:${minute} ${suffix.toUpperCase()}`
  return { date, time }
}

const SeniorAppointmentSummary = ({ cart, isLocalhost, demoTime, demoSession, demoPrice, upgradeFrom }) => {
  const orderPackages = (cart && cart.orderPackages
    ? cart.orderPackages.filter((op) => !!op.appointmentTime)
    : []
  )
  const hasReal = orderPackages.length > 0

  // No real data? Fall back to demo on localhost; otherwise render nothing.
  if (!hasReal && !isLocalhost) return null

  // If the localhost demo branch forwarded picked context (time + selected
  // session + diff price for upgrades), swap it into the placeholder so the
  // page reflects the customer's actual selection.
  const parsedDemo = parseDemoTime(demoTime)
  const demoPriceNum = demoPrice !== undefined && demoPrice !== null
    ? parseFloat(demoPrice)
    : null
  const demoSummary = {
    ...DEMO_SUMMARY,
    ...(parsedDemo ? { date: parsedDemo.date, time: parsedDemo.time } : {}),
    ...(demoSession ? {
      sessionName: demoSession,
      features: DEMO_FEATURES_BY_SESSION[demoSession] || DEMO_SUMMARY.features,
    } : {}),
    ...(Number.isFinite(demoPriceNum) ? { price: demoPriceNum } : {}),
  }

  const items = hasReal
    ? orderPackages.map((op) => {
        // Read the stored datetime as UTC (no local-TZ conversion). The
        // appointment_time column persists Pacific wall-clock digits with a
        // UTC label — so reading them as UTC preserves what the customer
        // picked. This is the senior-flow-only summary; the non-senior
        // review path renders <AppointmentSection /> instead and uses its
        // own (legacy) formatting.
        const apptDate = formatAppointmentDate(op.appointmentTime)
        const apptTime = formatAppointmentTime(op.appointmentTime)
        const features = ((op.apiOption && op.apiOption.description) || '')
          .split(',').map((s) => s.trim()).filter(Boolean)
        return {
          studentName: `${op.student.firstName} ${op.student.lastName}`,
          sessionName: (op.apiOption && op.apiOption.name) || 'Session',
          features,
          price: op.apiOption && op.apiOption.appointmentValue,
          date: apptDate,
          time: apptTime,
          schoolName: op.student.school && op.student.school.name,
          location: op.schoolId === 1075
            ? 'Shoob Studio · 4640 Spyres Way #1, Modesto, CA'
            : (op.student.school && op.student.school.scheduleLocation),
        }
      })
    : [demoSummary]

  const { subtotal, discount, tax, total } = computeSummaryTotals(hasReal ? cart : null, items)

  return (
    <div className={styles.seniorSummary}>
      <div className={styles.seniorSummaryNotice} role="note">
        <svg className={styles.seniorSummaryNoticeIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
        </svg>
        <span>
          {upgradeFrom ? (
            <>
              <strong>Your session upgrade isn't confirmed yet.</strong> The upgrade is
              confirmed once payment is processed.
            </>
          ) : (
            <>
              <strong>Your appointment isn't booked yet.</strong> Your time slot is
              reserved while you check out — the booking is confirmed once payment is
              processed.
            </>
          )}
        </span>
      </div>
      {items.map((item, idx) => (
        <div key={idx} className={styles.seniorSummaryItem}>
          <p className={styles.seniorSummaryEyebrow}>Senior Portrait Session</p>
          <h3 className={styles.seniorSummaryName}>{item.sessionName}</h3>
          {upgradeFrom && (
            <p className={styles.seniorSummaryUpgrade}>
              (upgrade from {upgradeFrom} Session)
            </p>
          )}
          {item.features.length > 0 && (
            <ul className={styles.seniorSummaryFeatures}>
              {item.features.map((f) => {
                const icon = featureIconFor(f)
                return (
                  <li key={f} className={icon ? styles.hasIcon : ''}>
                    {icon}
                    <span>{f}</span>
                  </li>
                )
              })}
            </ul>
          )}
          <dl className={styles.seniorSummaryDetails}>
            <div className={styles.seniorSummaryDetail}>
              <dt className={styles.seniorSummaryDetailLabel}>
                <svg className={styles.seniorSummaryDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Student
              </dt>
              <dd className={styles.seniorSummaryDetailValue}>{item.studentName}</dd>
            </div>
            <div className={styles.seniorSummaryDetail}>
              <dt className={styles.seniorSummaryDetailLabel}>
                <svg className={styles.seniorSummaryDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                </svg>
                Date
              </dt>
              <dd className={styles.seniorSummaryDetailValue}>{item.date}</dd>
            </div>
            <div className={styles.seniorSummaryDetail}>
              <dt className={styles.seniorSummaryDetailLabel}>
                <svg className={styles.seniorSummaryDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <polyline points="12 7 12 12 15 14" />
                </svg>
                Time
              </dt>
              <dd className={styles.seniorSummaryDetailValue}>{item.time}</dd>
            </div>
            <div className={styles.seniorSummaryDetail}>
              <dt className={styles.seniorSummaryDetailLabel}>
                <svg className={styles.seniorSummaryDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Location
              </dt>
              <dd className={styles.seniorSummaryDetailValue}>
                {item.schoolName}
                <span className={styles.seniorSummaryDetailSub}>{item.location}</span>
              </dd>
            </div>
          </dl>
        </div>
      ))}

      <div className={styles.seniorSummaryTotals}>
        <div className={styles.seniorSummaryRow}>
          <span>Subtotal</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        {discount > 0 && (
          <div className={cn(styles.seniorSummaryRow, styles.seniorSummaryDiscountRow)}>
            <span>Discount</span>
            <span>&minus;{formatPrice(discount)}</span>
          </div>
        )}
        <div className={styles.seniorSummaryRow}>
          <span>Tax</span>
          <span>{formatPrice(tax)}</span>
        </div>
        <div className={cn(styles.seniorSummaryRow, styles.seniorSummaryTotalRow)}>
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  )
}

// Demo-only shipping block for localhost previews. The real flow renders
// <ShippingForm /> after billing; the demo path has no cart, so we mirror the
// fields here so the senior checkout layout reflects the live behavior.
const SeniorDemoShipping = () => (
  <div className={styles.demoPaymentSection}>
    <h2 className={styles.header}>Shipping Info</h2>
    <div className={styles.demoCardForm}>
      <label className={styles.demoCardLabel}>
        Address Line 1
        <input type="text" autoComplete="off" />
      </label>
      <label className={styles.demoCardLabel}>
        Address Line 2 (Apt. / Unit #)
        <input type="text" autoComplete="off" />
      </label>
      <div className={styles.demoCardRow}>
        <label className={styles.demoCardLabel}>
          City
          <input type="text" autoComplete="off" />
        </label>
        <label className={styles.demoCardLabel}>
          State
          <input type="text" defaultValue="CA" autoComplete="off" />
        </label>
      </div>
      <label className={styles.demoCardLabel}>
        Zip Code
        <input type="text" inputMode="numeric" autoComplete="off" />
      </label>
    </div>
  </div>
)

// Demo-only payment block for localhost previews. Mirrors the structure of
// the real payment section (method radios → card form → Confirm button) so
// the senior checkout layout can be reviewed end-to-end without a real cart.
const SeniorDemoPayment = () => {
  const [method, setMethod] = useState('credit_card')
  return (
    <div className={styles.demoPaymentSection}>
      <h2 className={styles.header}>Payment</h2>

      <div className={styles.demoPaymentMethods}>
        <label className={styles.demoPaymentOption}>
          <input
            type="radio"
            name="demoPayment"
            value="credit_card"
            checked={method === 'credit_card'}
            onChange={() => setMethod('credit_card')}
          />
          <span>Credit Card</span>
        </label>
        <label className={styles.demoPaymentOption}>
          <input
            type="radio"
            name="demoPayment"
            value="paypal"
            checked={method === 'paypal'}
            onChange={() => setMethod('paypal')}
          />
          <span>PayPal</span>
        </label>
        <label className={styles.demoPaymentOption}>
          <input
            type="radio"
            name="demoPayment"
            value="afterpay"
            checked={method === 'afterpay'}
            onChange={() => setMethod('afterpay')}
          />
          <span>AfterPay / Clearpay</span>
        </label>
      </div>

      {method === 'credit_card' && (
        <div className={styles.demoCardForm}>
          <label className={styles.demoCardLabel}>
            Card number
            <input type="text" placeholder="•••• •••• •••• ••••" autoComplete="off" />
          </label>
          <div className={styles.demoCardRow}>
            <label className={styles.demoCardLabel}>
              Expires
              <input type="text" placeholder="MM / YY" autoComplete="off" />
            </label>
            <label className={styles.demoCardLabel}>
              CVC
              <input type="text" placeholder="CVC" autoComplete="off" />
            </label>
          </div>
          <label className={styles.demoCardLabel}>
            Name on card
            <input type="text" autoComplete="off" />
          </label>
        </div>
      )}

      {method !== 'credit_card' && (
        <p className={styles.demoAltMethodNote}>
          You'll be redirected to {method === 'paypal' ? 'PayPal' : 'AfterPay'} to complete payment.
        </p>
      )}
    </div>
  )
}

// 4-step booking indicator for the senior portrait flow. Confirm (step 4) is
// active here — student/session/date were chosen on the previous screens.
const SeniorSteps = () => (
  <ol className={styles.seniorSteps} aria-label="Booking progress">
    <li className={cn(styles.seniorStepItem, styles.seniorStepItemDone)}>
      <span className={styles.seniorStepNum}>
        <svg className={styles.seniorStepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={styles.seniorStepLabel}>Find your student</span>
    </li>
    <span className={cn(styles.seniorStepConnector, styles.seniorStepConnectorDone)} aria-hidden="true" />
    <li className={cn(styles.seniorStepItem, styles.seniorStepItemDone)}>
      <span className={styles.seniorStepNum}>
        <svg className={styles.seniorStepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={styles.seniorStepLabel}>Choose your session</span>
    </li>
    <span className={cn(styles.seniorStepConnector, styles.seniorStepConnectorDone)} aria-hidden="true" />
    <li className={cn(styles.seniorStepItem, styles.seniorStepItemDone)}>
      <span className={styles.seniorStepNum}>
        <svg className={styles.seniorStepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={styles.seniorStepLabel}>Choose date/time</span>
    </li>
    <span className={cn(styles.seniorStepConnector, styles.seniorStepConnectorDone)} aria-hidden="true" />
    <li className={cn(styles.seniorStepItem, styles.seniorStepItemActive)}>
      <span className={styles.seniorStepNum}>
        <svg className={styles.seniorStepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 12 10 17 19 7"/>
        </svg>
      </span>
      <span className={styles.seniorStepLabel}>Confirm</span>
    </li>
  </ol>
)

const Checkout = () => {
  const { app, senior, demoTime, demoSession, demoPrice, upgradeFrom } = useQueryString()
  // Only the appointment-booking flow gets the senior restyle — the senior
  // portrait *package* order flow can also carry `senior=true` and must
  // keep its existing layout. Both flags together (set by the booking
  // pages) is the unambiguous signal for the appointment confirm page.
  const isSeniorBooking = !!senior && !!app
  const isLocalhost = typeof window !== 'undefined'
    && /^(localhost|127\.|.*\.local)$/.test(window.location.hostname)
  const history = useHistory()
  const { register, handleSubmit, control, trigger, watch, setValue, unregister, formState: { errors } } = useForm();
  const { customer, setCustomer } = useContext(CustomerContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderErrors, setOrderErrors] = useState([]);
  const [callbackErrors, setCallbackErrors] = useState([]);
  const [showBilling, setShowBilling] = useState(false);
  const [showShipping, setShowShipping] = useState(false);
  const [usStates, setUsStates] = useState([]);
  const stripe = useStripe();
  const cartKey = app ? 'apptCart' : (customer['priorityCart'] ? 'priorityCart' : 'cart')
  const params = new URLSearchParams(useLocation().search);
  const clientSecret = params.get('payment_intent_client_secret');

  // Redirect admins to test checkout
  useEffect(() => {
    const checkAdminAndRedirect = async () => {
      try {
        const response = await fetch('/api/v1/users/me');
        const data = await response.json();

        if (data?.data?.admin || data?.data?.role === 'admin') {
          history.push('/test-checkout');
        }
      } catch (error) {
        // If the API call fails or user is not logged in, just continue with normal checkout
        console.log('User check failed, continuing with normal checkout');
      }
    };

    checkAdminAndRedirect();
  }, [history]);

  const watchFirstName = watch('first_name');

  useEffect(() => {
    if (!customer[cartKey]) return

    const students = customer[cartKey]
      .orderPackages
      .map((op) => op.student)
      .filter((student) => !!student)

    if (students.some((student) => student.firstName.toLowerCase() === (watchFirstName || '').toLowerCase())) {
      alert('Please make sure your first name is entered and not the student.')
    }
  }, [watchFirstName])

  useEffect(() => {
    const fetchStates = async () => {
      const statesRes = await getStates();

      if (statesRes.status !== 200) return;

      const formattedStates = statesRes.data.data.map(state => ({ value: state[1], label: state[1] }))
      setUsStates(formattedStates);
    };

    fetchStates();
  }, []);

  useEffect(() => {
    if (customer[cartKey]) return;
    if (clientSecret) return;
    // Visual-preview bypass: any `demo*` query param means this is a
    // localhost preview of the page layout, not a real checkout. Let the
    // demo render instead of redirecting to home.
    if (demoTime || demoSession || demoPrice || upgradeFrom) return;

    history.push('/');
  }, []);

  // If the user is returning from Clearpay, capture the client secret and create the order
  useEffect(() => {
    if(!stripe || !clientSecret) {
      return;
    }

    fetchPaymentIntent({ stripe, setCallbackErrors, cartKey, setIsSubmitting, setCustomer, setShowBilling,
      setValue, customer, clientSecret, history
    });
  }, [stripe, clientSecret])

  const createPayPalOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: (cart && cart.apiTotal.toFixed(2)) || '0.1',
          },
        },
      ],
    });
  };

  const renderSubmitButton = () => {
    if (watchPaymentMethod !== 'clearpay') {
      return (
        <>
          {isSeniorBooking && (
            <p className={styles.seniorPayNotice}>
              {upgradeFrom
                ? 'The upgrade is confirmed once payment is processed.'
                : 'Your appointment time is held while you finish checkout. The booking is confirmed once payment is processed.'}
            </p>
          )}
          <Button
            fullWidth
            variant='submit'
            onClick={isSeniorBooking && isLocalhost ? (e) => {
              // Localhost senior flow has no real payment backend wired up.
              // Stop the form submit and surface a visible demo notice so
              // clicking the button isn't silently no-op.
              e.preventDefault()
              alert('Demo: this is where the customer would proceed to billing/checkout.')
            } : undefined}
            disabled={isSubmitting}
          >
            {isSeniorBooking ? 'Proceed to Checkout' : 'Place Order'}
          </Button>
        </>
      )
    }

    return (
      <Button
        fullWidth
        variant='submit'
        disabled={isSubmitting}
      >
        <span className={styles.afterpayLogo}>
          Continue with <Image src={afterpayLogo} />
        </span>
      </Button>
    )
  }

  const billingWrapper = () => {
    // A $0 total submits directly with no payment. For a senior sitting this also
    // covers a coupon that brought the total to $0 (100% off) -- a sitting ships
    // nothing, so skip the billing/shipping steps and book it directly. Non-senior
    // $0-with-coupon orders keep the existing shipping path (free physical goods
    // still need a ship-to address).
    if (cart.apiTotal === 0 && (!cart.couponId || isSeniorBooking)) {
      const isSeniorDemo = isSeniorBooking && isLocalhost
      return (
        <Button
          fullWidth
          variant='submit'
          onClick={isSeniorDemo ? (e) => {
            // Localhost senior flow has no real payment backend wired up.
            // Stop the form submit and surface a visible demo notice so
            // clicking the button isn't silently no-op.
            e.preventDefault()
            alert('Demo: this is where the free sitting would be booked.')
          } : undefined}
          disabled={isSubmitting}
        >
          {isSeniorBooking ? 'Complete Booking' : 'Place Order'}
        </Button>
      )
    }

    if (freePackageFromCoupon()) {
      renderShippingButton();
    }

    return (
      renderBillingButton()
    )
  }
  const renderBillingButton = () => {
    if (freePackageFromCoupon()) return;
    if (showBilling || showShipping) return;

    return (
      <Grid container justify='center' spacing={2}>
        <Grid item xs={12} md={12}>
          <Button
            classes={styles.btnContainer}
            fullWidth
            variant='primary'
            onClick={() => validateCustomerInfo({ trigger, setCustomer, customer, watch, setShowBilling, errors })}
          >
            Continue to Billing
          </Button>
        </Grid>
      </Grid>
    )
  }

  const renderShippingButton = () => {
    if (showShipping && freePackageFromCoupon()) return
    if (showBilling && showShipping && watchPaymentMethod && !freePackageFromCoupon()) return

    return (
      <Grid container justify='center' spacing={2}>
        <Grid item xs={12} style={{ padding: '1rem' }} md={12}>
          <Button
            fullWidth
            variant='primary'
            onClick={() => validatePaymentMethod({ trigger, errors, setShowShipping })}
          >
            Continue to {isYearbookOnlyOrder ? 'Payment' : 'Shipping'}
          </Button>
        </Grid>
      </Grid>
    )
  }

  const onSubmit = async(data) => {
    setIsSubmitting(true);

    let payPalOrderId;
    let paidWithPayPal;

    // Validate order if the purchase is made with a vendor (Stripe, PayPal) to make sure
    // the order is valid before capturing
    if (window.paypalOrder || window.stripeOrder || watchPaymentMethod === 'clearpay') {
      const initialOrderData = assignOrderData({ data, customer, cartKey,
        payPalOrderId: JSON.stringify(window.paypalOrder || window.stripeOrder || (watchPaymentMethod === 'clearpay' && { clearPay: true })),
        paidWithPayPal: true
      })

      const { orderIsValid, orderErrors } = await validateNewOrder({ initialOrderData });

      if (!orderIsValid) {
        setOrderErrors([orderErrors || 'There was an error processing your order. Please try again or use another payment method.'])
        setIsSubmitting(false);

        return;
      }
    }

    // If Clearpay was selected, redirect them to Clearpay to enter in the payment information
    if (watchPaymentMethod === 'clearpay') {
      await handleClearpay({ data, cart, setOrderErrors, setIsSubmitting, stripe, watchSameAsBilling, customer });
      return;
    }

    // Capture PayPal order
    if (window.paypalOrder) {
      await window.paypalOrder.capture().then((details) => {
        payPalOrderId = JSON.stringify(details);
        paidWithPayPal = true;
        window.paypalOrder = undefined;

        if (details.status !== 'COMPLETED') {
          setOrderErrors(['There was an error processing the PayPal payment. Please try again or use another payment method.'])
          setValue("payment_method", undefined);
          setIsSubmitting(false);

          return;
        }
      })
    // Capture Google Pay/Apple Pay order
    } else if (window.stripeOrder) {
      const {
        error: stripeError,
        paymentIntent,
      } = await stripe.confirmCardPayment(window.stripeKey, {
        payment_method: window.stripeOrder.paymentMethod.id,
      }, { handleActions: false });

      if (stripeError) {
        setOrderErrors([stripeError.message || 'There was an error processing your payment. Please try again or use another payment method.'])
        setValue("payment_method", undefined);
        window.stripeOrder = undefined;
        window.stripeKey = undefined;
        setIsSubmitting(false);

        return;
      }

      if (paymentIntent.status === 'requires_action') {
        stripe.confirmCardPayment(window.stripeKey);
      }

      if (paymentIntent.status !== 'succeeded') {
        setOrderErrors(['There was an error processing your payment. Please try again or use another payment method.'])
        setIsSubmitting(false);
        window.stripeOrder = undefined;
        window.stripeKey = undefined;

        return;
      }

      payPalOrderId = JSON.stringify(window.stripeOrder);
      paidWithPayPal = true;
      window.stripeOrder = undefined;
      window.stripeKey = undefined;
    }

    let formattedData = assignOrderData({ data, customer, cartKey, payPalOrderId, paidWithPayPal });

    createOrder({ order: formattedData })
      .then((response) => {
        setCustomer({ ...customer, cart: null })
        history.push(`/order/complete/${response.data.order.id}/${response.data.order.email}?app=${app}`)
        customer[cartKey] = null;
      })
      .catch((error) => {
        setOrderErrors((error.response && error.response.data && error.response.data.data) || ['Something went wrong. Please try again. You have not been charged.']);
      })
      .then(() => {
        setIsSubmitting(false);
      });
  };

  const [couponRefresh, setCouponRefresh] = useState(0);
  const cart = useCart(customer, couponRefresh);
  const packages = usePackages(cart, customer)

  // Yearbooks shipped to the home (ship_to_mail) must still collect a shipping
  // address, so don't treat them as "yearbook only" (which skips the shipping
  // step) — let them flow through the normal billing -> shipping -> payment path.
  const shipsToHome = (cart?.orderPackages || []).some((op) => op.shipDestination === 'ship_to_mail')
  const isYearbookOnlyOrder = packages.reduce((acc, cur) => acc && cur.isYearbook(), true) && !shipsToHome
  const watchPaymentMethod = watch("payment_method", '');
  const watchSameAsBilling = watch("same_as_billing", false);
  const freePackageFromCoupon = () => cart && cart.apiTotal === 0 && cart.couponId && cart.couponId.length !==0;
  // A senior sitting whose total is $0 (free session or a 100%-off coupon): book
  // directly, no billing/shipping. billingWrapper renders the direct submit button
  // for this case, so the shipping/payment sections below are suppressed.
  const isFreeSitting = isSeniorBooking && !!cart && cart.apiTotal === 0;

  return (
    <div className={cn({
      [styles.appWrapper]: app,
      [styles.page]: true,
      [styles.seniorWrap]: isSeniorBooking,
    })}>
      {isSeniorBooking && <SeniorSteps />}
      {app && (
        <div className={styles.apptSection}>
          {!isSeniorBooking && (
            <h1 className={styles.header}>1. Review Appointment</h1>
          )}
          {isSeniorBooking
            ? (
              <>
                <SeniorAppointmentSummary cart={cart} isLocalhost={isLocalhost} demoTime={demoTime} demoSession={demoSession} demoPrice={demoPrice} upgradeFrom={upgradeFrom} />
                {cart && (
                  <div className={styles.seniorCoupon}>
                    <p className={styles.seniorCouponLabel}>Have a coupon?</p>
                    <CouponForm appointments onApplied={() => setCouponRefresh((n) => n + 1)} />
                  </div>
                )}
              </>
            )
            : (cart && <AppointmentSection cart={cart} />)
          }
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Grid container justify='center' classes={styles.container} maxWidth='md'>
          {callbackErrors.length !== 0 && <ErrorContainer errors={callbackErrors} />}

          <Grid style={{ padding: isSeniorBooking ? '0 1rem 1rem' : '1rem' }} item xs={12}>
            {!app && (
              <h2 className={styles.header}>
                Checkout
              </h2>
            )}

            <CustomerInfo
              app={app}
              isSeniorBooking={isSeniorBooking}
              customer={customer}
              errors={errors}
              register={register}
              control={control}
              setValue={setValue}
              trigger={trigger}
              watch={watch}
            />

            {isSeniorBooking && isLocalhost && !cart && (
              <>
                <SeniorDemoShipping />
                <SeniorDemoPayment />
                <p className={styles.seniorPayNotice}>
                  {upgradeFrom
                    ? 'Your existing appointment time stays the same. The upgrade is confirmed once payment is processed.'
                    : 'Your appointment time is held while you finish checkout. The booking is confirmed once payment is processed.'}
                </p>
                <Button
                  fullWidth
                  variant='submit'
                  onClick={(e) => { e.preventDefault(); alert('Demo: this is where the order would be submitted.') }}
                >
                  Confirm & Pay
                </Button>
              </>
            )}

            {cart && billingWrapper()}

            {showBilling && (
              <>
                <h2 className={styles.header}>
                  {app ? '3' : '2'}. Payment
                </h2>

                <div className={styles.paymentSelect}>
                  <div className={styles.radioGroup}>
                    <label className={styles.paymentLabel}>
                      <input
                        {...register("payment_method", { required: true, message: "Please select a payment method" })}
                        type="radio"
                        value="credit_card" />

                      <span>
                        Credit Card <Image src={acceptedCards} />
                      </span>
                    </label>
                  </div>

                  <div className={styles.radioGroup}>
                    <label className={styles.paymentLabel}>
                      <input
                        {...register("payment_method", { required: true })}
                        type="radio"
                        onChange={() => unregister(
                          ["name_on_card", "card_number", "card_expires_on", "card_verification",
                            "address", "address2", "city", "state", "zip_code"])}
                        value="paypal" />
                      <span>
                        <PayPal
                          createOrder={createPayPalOrder}
                          onApprove={(data, actions) => onPayPalApprove({ data, actions, setValue, setShowShipping, usStates })}
                        />
                      </span>
                    </label>
                  </div>

                  {cart && (
                    <Stripe
                      cart={cart}
                      setValue={setValue}
                      setShowShipping={setShowShipping}
                      usStates={usStates}
                      setOrderErrors={setOrderErrors}
                      handleSubmit={handleSubmit}
                      onSubmit={onSubmit}
                      unregister={unregister}
                      register={register}
                      skipShipping={false}
                    />)
                  }

                  {canOfferAfterpay({ cart, app, stripe }) && (
                    <div className={styles.radioGroup}>
                      <label className={styles.paymentLabel}>
                        <input
                          {...register("payment_method", { required: true, message: "Please select a payment method" })}
                          type="radio"
                          value="clearpay" />
                        <span className={styles.clearPay}>
                        {/* Display only - the instalment preview, not a charge - but it
                            used the same float-truncating expression, so it shares the one
                            toCents helper rather than becoming the next stale copy. */}
                        <AfterpayClearpayMessageElement options={{amount: toCents(cart.apiTotal), currency: 'USD'}} />
                      </span>
                      </label>
                    </div>
                  )}
                </div>

                {errors.payment_method && <p className={styles.errorMessage}>Please select a payment method</p>}

                {(watchPaymentMethod === 'credit_card' || watchPaymentMethod === 'clearpay') && (
                  <CardForm
                    app={app}
                    register={register}
                    errors={errors}
                    control={control}
                    usStates={usStates}
                    setValue={setValue}
                    trigger={trigger}
                    isClearpay={watchPaymentMethod === 'clearpay'}
                  />
                )}
              </>
            )}

            {((showBilling && watchPaymentMethod) || freePackageFromCoupon()) && !isFreeSitting && renderShippingButton()}

            {showShipping && !isYearbookOnlyOrder && (
              <ShippingForm
                app={app}
                cardPayment={watchPaymentMethod === 'credit_card'}
                clearpayPayment={watchPaymentMethod === 'clearpay'}
                register={register}
                errors={errors}
                control={control}
                sameAsBilling={watchSameAsBilling === true}
                unregister={unregister}
                usStates={usStates}
                setValue={setValue}
              />
            )}
          </Grid>
        </Grid>

        {(showShipping && (showBilling || freePackageFromCoupon())) && !isFreeSitting && (
          <>
            <div style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between' }}>
              <p className={styles.total}>TOTAL</p>
              <p className={styles.total}>${cart && cart.apiTotal.toFixed(2)}</p>
            </div>

            {orderErrors.length !== 0 && <ErrorContainer errors={orderErrors} />}

            <Grid container justify='center' spacing={2}>
              <Grid item xs={12} md={12} style={{ padding: '0 1rem' }}>
                <Hr />
              </Grid>
              <Grid item xs={12} md={12} style={{ padding: '0 1rem' }}>
                {renderSubmitButton()}
              </Grid>
            </Grid>
          </>
        )}

        <Grid container justify='center' spacing={2}>
          <Grid item xs={12} md={12} style={isSeniorBooking ? { padding: '0 1rem' } : undefined}>
            {isSeniorBooking ? (
              <Button
                classes={styles.seniorChangeSessionBtn}
                fullWidth
                variant=""
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  // Navigate back to the session-selection page so the
                  // customer can pick a different option (Super / Deluxe /
                  // Standard / etc.) before committing to checkout.
                  const pkgId = customer.currentOrderPackage?.packageId
                    || customer.currentOrderPackage?.package?.id
                  if (pkgId) {
                    history.push(routes.appointmentOptionSelection(pkgId, { senior: 'true' }))
                  } else {
                    history.goBack()
                  }
                }}
              >
                <svg className={styles.seniorChangeSessionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                <span>Change Session Selection</span>
              </Button>
            ) : (
              <Button
                classes={styles.btnContainer}
                fullWidth
                variant="link"
                type="button"
                onClick={(e) => { e.preventDefault(); history.goBack(); }}
              >
                Back
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
    </div>
  );
}

export default Checkout;
